import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

import { AccessGuard } from '@app/access/access-guard';
import { EAccess } from '@app/access/access.model';
import { EFormTriggerAlertStatus, IFormTriggerAlertRow } from '@app/models/form-triggers/form-trigger.model';
import { AuthenticationService } from '@core/services/auth.service';
import { FormTriggerService } from './form-trigger.service';

/**
 * Shared open high-risk-alert state feeding every notification surface (topbar bell,
 * sidebar badge, dashboard banner). The first concrete feed of the notification
 * system - future notification types join here rather than polling separately.
 */
@Injectable({ providedIn: 'root' })
export class AlertNotificationService {
  public readonly openCount$ = new BehaviorSubject<number>(0);
  public readonly openAlerts$ = new BehaviorSubject<IFormTriggerAlertRow[]>([]);
  /** True when the current user can see alerts at all (project toggle + view access). */
  public readonly available$ = new BehaviorSubject<boolean>(false);

  constructor(
    router: Router,
    private auth: AuthenticationService,
    private formTriggerService: FormTriggerService
  ) {
    this.auth.currentUserObs.subscribe(() => this.refresh());
    // Keep the count live as the user moves around, without polling on a timer.
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        throttleTime(10000)
      )
      .subscribe(() => this.refresh());
  }

  public refresh(): void {
    const user = this.auth.currentUser as any;
    const permitted = !!user?.riskAlertsEnabled && AccessGuard.hasAccess(EAccess.FormTriggerAlert);
    if (!permitted) {
      this.available$.next(false);
      this.openCount$.next(0);
      this.openAlerts$.next([]);
      return;
    }

    this.formTriggerService
      .getAlerts({ statuses: [EFormTriggerAlertStatus.Open], page: 1, pageSize: 5 })
      .subscribe({
        next: (res) => {
          // The server decides real visibility: admins see everything; everyone else only
          // sees the section while assigned to at least one trigger.
          const canView = res?.canView !== false;
          this.available$.next(canView);
          this.openCount$.next(canView ? (res?.openCount ?? 0) : 0);
          this.openAlerts$.next(canView ? (res?.rows ?? []) : []);
        },
        error: () => {
          // Leave the last known state - a transient failure must not flap the surfaces.
        }
      });
  }
}
